import { ObjectTools } from '../../tools/object-tools';

export interface SolLoaderRow {
  width?: SolLoaderRowWidth; // Default '100'%
  cells: SolLoaderCell[];
}

export interface SolLoaderCell {
  height?: SolLoaderRowHeight; // Default '32'px
}

export type SolLoaderRowHeight = '16' | '32' | '64' | '128' | '256';
export type SolLoaderRowWidth = '50' | '100';
export type SolLoaderGap = '4' | '8' | '16';
export type SolLoaderMargin = '0' | '16';

export class SolLoader {
  private constructor(
    public readonly rows: SolLoaderRow[],
    public readonly gap: SolLoaderGap,
    public readonly margin: SolLoaderMargin,
    public readonly frozen: boolean,
  ) {}

  public static of(
    rows: SolLoaderRow[],
    gap: SolLoaderGap,
    margin: SolLoaderMargin,
    frozen: boolean = false,
  ): SolLoader {
    return new SolLoader(rows, gap, margin, frozen);
  }

  public static ofTable(columnCount: number = 4, rowCount: number = 6): SolLoader {
    const rows: SolLoaderRow[] = [];
    for (let r = 0; r < rowCount; r++) {
      const cells: SolLoaderCell[] = [];
      for (let c = 0; c < columnCount; c++) {
        cells.push({});
      }
      rows.push({ cells });
    }
    return SolLoader.of(rows, '8', '16');
  }

  public static ofList(itemCount: number = 3, groupCount: number = 0): SolLoader {
    const rows: SolLoaderRow[] = [];
    for (let i = 0; i < Math.max(groupCount, 1); i++) {
      if (groupCount > 0) {
        rows.push({
          width: '50',
          cells: [{}],
        });
      }
      for (let j = 0; j < itemCount; j++) {
        rows.push({ cells: [{}] });
      }
    }
    return SolLoader.of(rows, '8', '16');
  }

  public static ofChart(): SolLoader {
    return SolLoader.of(
      [
        {
          cells: [
            { height: '32' },
            { height: '32' },
            { height: '64' },
            { height: '128' },
            { height: '32' },
            { height: '16' },
            { height: '64' },
          ],
        },
      ],
      '8',
      '16',
    );
  }

  public static ofBlocks(rowCount: number = 2, columnCount: number = 1, height: SolLoaderRowHeight = '128'): SolLoader {
    const rows: SolLoaderRow[] = [];
    for (let i = 0; i < rowCount; i++) {
      const cells: SolLoaderCell[] = [];
      for (let j = 0; j < columnCount; j++) {
        cells.push({ height });
      }
      rows.push({ cells });
    }
    return SolLoader.of(rows, '8', '16');
  }

  public withRows(rows: SolLoaderRow[]): SolLoader {
    return this.copy({ rows });
  }

  public withGap(gap: SolLoaderGap): SolLoader {
    return this.copy({ gap });
  }

  public withMargin(margin: SolLoaderMargin): SolLoader {
    return this.copy({ margin });
  }

  public withFrozen(frozen: boolean): SolLoader {
    return this.copy({ frozen });
  }

  private copy(props: Partial<SolLoader>): SolLoader {
    return ObjectTools.copy<SolLoader>(this, props);
  }
}
